import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Download, Search, FileText, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const dateField = "h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";
const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-xs font-medium mb-1 text-muted-foreground";

const fyStart = () => {
  const d = new Date();
  const y = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-04-01`;
};

export default function EmployeeLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [data, setData] = useState(null);
  const [start, setStart] = useState(fyStart());
  const [end, setEnd] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ date: today(), particulars: "", debit: "", credit: "" });

  const [empAssets, setEmpAssets] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = { format: "json" };
      if (start) params.start = start;
      if (end) params.end = end;
      setData((await api.get(`/employees/${id}/ledger`, { params })).data);
    } catch (e) { toast.error(`Could not load employee ledger${e.response?.data?.detail ? `: ${formatApiError(e.response.data.detail)}` : e.response?.status ? ` (HTTP ${e.response.status})` : ""}`); }
  }, [id, start, end]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/employees/${id}/assets`).then((r) => setEmpAssets(r.data || [])).catch(() => setEmpAssets([])); }, [id]);

  const addEntry = async () => {
    const debit = parseFloat(form.debit) || 0;
    const credit = parseFloat(form.credit) || 0;
    if (!form.date) return toast.error("Pick a date");
    if (debit <= 0 && credit <= 0) return toast.error("Enter a debit or credit amount");
    try {
      await api.post("/ledger-entries", { scope: "employee", target_id: id, date: form.date, particulars: form.particulars, debit, credit });
      toast.success("Entry added");
      setForm({ date: form.date, particulars: "", debit: "", credit: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delEntry = async (mid) => {
    if (!window.confirm("Delete this manual entry?")) return;
    try { await api.delete(`/ledger-entries/${mid}`); toast.success("Entry deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.rows;
    return data.rows.filter((r) =>
      String(r.ref).toLowerCase().includes(term) || (r.particulars || "").toLowerCase().includes(term) || (r.date || "").includes(term));
  }, [data, q]);

  const download = async (format) => {
    try {
      const params = { format };
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/employees/${id}/ledger`, { params, responseType: "blob" });
      const mime = format === "csv" ? "text/csv" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee_ledger_${(data?.employee?.name || "employee").replace(/\s/g, "_")}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch { toast.error("Download failed"); }
  };

  const e = data?.employee;
  const closing = data?.rows?.length ? data.rows[data.rows.length - 1].balance : 0;

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <button data-testid="emp-ledger-back" onClick={() => navigate("/salary")}
          className="mb-4 h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
          <ArrowLeft size={14} /> Back to Salary
        </button>

        <PageHeader title={e?.name || "Employee Ledger"} subtitle={e?.designation ? `${e.designation} · general ledger` : "General ledger statement"}>
          <div className="flex gap-2">
            <button data-testid="emp-ledger-pdf" onClick={() => download("pdf")}
              className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Download size={15} /> PDF</button>
            <button data-testid="emp-ledger-csv" onClick={() => download("csv")}
              className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><FileText size={15} /> Excel/CSV</button>
            <button data-testid="emp-clearance-pdf" onClick={async () => {
              try {
                const res = await api.get(`/employees/${id}/clearance`, { params: { format: "pdf" }, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a"); a.href = url; a.download = `exit_clearance_${(e?.name || "employee").replace(/\s/g, "_")}.pdf`; a.click();
                window.URL.revokeObjectURL(url); toast.success("Clearance downloaded");
              } catch { toast.error("Download failed"); }
            }} className="h-10 px-4 rounded-md border border-amber-500/40 bg-amber-500/5 text-amber-600 text-sm flex items-center gap-2 hover:bg-amber-500/10 transition-colors"><AlertTriangle size={15} /> Exit clearance</button>
          </div>
        </PageHeader>

        {empAssets.length > 0 && (
          <div data-testid="emp-assets-card" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-5 animate-fade-up">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2 text-amber-600">
              <AlertTriangle size={15} /> Assets allotted — recover these before the employee leaves ({empAssets.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {empAssets.map((a) => (
                <div key={a.id} data-testid={`emp-asset-${a.id}`} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
                  <span className="num text-xs font-bold text-primary mr-1.5">{a.asset_code}</span>{a.name}
                  <span className="text-xs text-muted-foreground ml-1">· {a.category}{a.allotted_date ? ` · since ${a.allotted_date}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {canWrite && (
          <div data-testid="emp-ledger-add" className="rounded-xl border border-border bg-card p-4 mb-5 animate-fade-up">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Plus size={15} /> Add general entry (for history before your cash book starts)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <div><label className={label}>Date</label>
                <input data-testid="emp-entry-date" type="date" className={field + " num"} value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })} /></div>
              <div className="col-span-2 md:col-span-2"><label className={label}>Particulars</label>
                <input data-testid="emp-entry-particulars" className={field} placeholder="e.g. Salary March, Advance…" value={form.particulars} onChange={(ev) => setForm({ ...form, particulars: ev.target.value })} /></div>
              <div><label className={label}>Debit (paid to employee)</label>
                <input data-testid="emp-entry-debit" type="number" step="0.01" min="0" className={field + " num"} placeholder="0.00" value={form.debit} onChange={(ev) => setForm({ ...form, debit: ev.target.value })} /></div>
              <div><label className={label}>Credit (salary earned)</label>
                <input data-testid="emp-entry-credit" type="number" step="0.01" min="0" className={field + " num"} placeholder="0.00" value={form.credit} onChange={(ev) => setForm({ ...form, credit: ev.target.value })} /></div>
            </div>
            <div className="mt-3 flex justify-end">
              <button data-testid="emp-entry-save" onClick={addEntry} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Add entry</button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="emp-ledger-search" value={q} onChange={(ev) => setQ(ev.target.value)}
              placeholder="Search particulars…"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          </div>
          <div><div className="text-[11px] text-muted-foreground mb-0.5">From</div>
            <input data-testid="emp-ledger-start" type="date" className={dateField} value={start} onChange={(ev) => setStart(ev.target.value)} /></div>
          <div><div className="text-[11px] text-muted-foreground mb-0.5">To</div>
            <input data-testid="emp-ledger-end" type="date" className={dateField} value={end} onChange={(ev) => setEnd(ev.target.value)} /></div>
          {(start || end) && (
            <button data-testid="emp-ledger-clear" onClick={() => { setStart(""); setEnd(""); }}
              className="h-9 px-3 rounded-md border border-input bg-card text-xs hover:bg-muted transition-colors">Clear</button>
          )}
          {data && (
            <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-2 ml-auto">
              <div className="text-xs text-muted-foreground">Balance payable</div>
              <div data-testid="emp-ledger-closing" className="num text-lg font-bold text-accent">{inr(closing)}</div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
          <div className="min-w-[960px]">
            <div className="grid grid-cols-[96px_110px_1fr_110px_110px_120px_24px] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Date</span><span>Ref</span><span>Particulars</span><span className="text-right">Debit</span><span className="text-right">Credit</span><span className="text-right">Balance</span><span></span>
            </div>
            <div className="divide-y divide-border">
              {rows.map((r, i) => (
                <div key={i} data-testid={`emp-ledger-row-${i}`} className="grid grid-cols-[96px_110px_1fr_110px_110px_120px_24px] gap-3 px-5 py-2.5 items-center text-sm">
                  <span className="num text-muted-foreground">{r.date}</span>
                  <span className="num text-xs text-primary">{r.ref}</span>
                  <span data-testid={`emp-ledger-particulars-${i}`} className={`break-words ${r.provisional ? "italic text-muted-foreground" : ""}`}>{r.particulars}</span>
                  <span className="num text-right text-destructive">{r.debit ? inr(r.debit) : "—"}</span>
                  <span className="num text-right text-[hsl(var(--positive))]">{r.credit ? inr(r.credit) : "—"}</span>
                  <span className="num text-right font-semibold">{inr(r.balance)}</span>
                  <span className="text-right">
                    {r.manual_id && canWrite && (
                      <button data-testid={`emp-ledger-del-${i}`} onClick={() => delEntry(r.manual_id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={13} /></button>
                    )}
                  </span>
                </div>
              ))}
              {rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No entries{q ? " match your search" : ""}.</div>}
            </div>
            {data && (
              <div className="grid grid-cols-[96px_110px_1fr_110px_110px_120px_24px] gap-3 px-5 py-3 bg-muted text-sm font-bold">
                <span></span><span></span><span className="text-right">Closing balance</span>
                <span className="num text-right text-destructive">{inr(data.debit)}</span>
                <span className="num text-right text-[hsl(var(--positive))]">{inr(data.credit)}</span>
                <span className="num text-right">{inr(closing)}</span>
                <span></span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
